import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogContentText, 
  DialogActions, 
  Button, 
  CircularProgress,
  Chip,
  Paper,
  Menu,
  MenuItem,
  Fade,
  Skeleton,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery,
  Alert
} from '@mui/material';
import HotelIcon from '@mui/icons-material/Hotel';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import GroupIcon from '@mui/icons-material/Group';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { MenuItem as MuiMenuItem, TextField, FormControl, InputLabel, Select, Autocomplete } from '@mui/material';
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';
import axios from 'axios';
import AmenitiesModal from '../components/AmenitiesModal';
import amenitiesSections from '../components/amenitiesData';
import dayjs from 'dayjs';
import useCustomToast from '../hooks/useCustomToast';

const SECRET_KEY = 'tabtrips_hotel_secret';
const TOKEN_COOKIE_KEY = 'token';

function getDecryptedToken() {
  const encrypted = Cookies.get(TOKEN_COOKIE_KEY);
  if (!encrypted) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
}

/**
 * React Strict Mode dev में effect दो बार चला देता है — दोनों बार एक ही in-flight
 * promise share करें ताकि असली नेटवर्क पर सिर्फ़ एक getAllRoomTypes + एक getHotelDetails जाए।
 * AbortController हटाया गया: रद्द कॉल DevTools में लाल / (canceled) दिखती है।
 */
const roomTypesPageInFlight = new Map();

function fetchRoomTypesPageDataOnce(token, hotelId) {
  const hid = parseInt(hotelId, 10);
  const key = `${hid}::${token}`;
  const cached = roomTypesPageInFlight.get(key);
  if (cached) return cached;

  const promise = (async () => {
    const response = await axios.post(
      'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getAllRoomTypes',
      { token, hotel_id: hid },
      { headers: { 'Content-Type': 'application/json' } }
    );

    const rooms =
      response.data?.status && Array.isArray(response.data.rooms_details)
        ? response.data.rooms_details
        : [];

    let hotelDetailsResponse = null;
    if (response.data?.status && rooms.length > 0) {
      const formData = new FormData();
      formData.append('hotel_id', String(hotelId));
      formData.append('token', token);
      hotelDetailsResponse = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getHotelDetails',
        formData
      );
    }

    return { response, hotelDetailsResponse };
  })();

  roomTypesPageInFlight.set(key, promise);
  promise.finally(() => {
    setTimeout(() => roomTypesPageInFlight.delete(key), 400);
  });

  return promise;
}

/** Autocomplete / API: underscores → spaces, lowercase, collapse whitespace & punctuation */
function normalizeRoomSearchText(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/[\u00A0\u2000-\u200B\u202F\u205F\u3000]/g, ' ')
    .replace(/[''`]/g, '')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function getRoomOptionSearchableText(option) {
  return normalizeRoomSearchText(
    [option.safe_name, option.name, option.type, option.category_name]
      .filter(Boolean)
      .join(' ')
  );
}

/** Token match (any order) OR full pasted/typed phrase is a substring of the room name */
function roomOptionMatchesQuery(option, inputValue) {
  const query = normalizeRoomSearchText(inputValue);
  if (!query) return true;

  const hay = getRoomOptionSearchableText(option);
  if (!hay) return false;

  if (hay.includes(query) || query.includes(hay)) return true;

  const terms = query.split(/\s+/).filter((t) => t.length > 0);
  if (!terms.length) return true;

  return terms.every((t) => hay.includes(t));
}

function roomOptionMatchesSearchTokens(option, tokens) {
  return roomOptionMatchesQuery(option, tokens.join(' '));
}

const RoomTypes = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // Helper function to check if room type is active
  const isRoomActive = (room) => {
    if (!room || room.isActive === undefined || room.isActive === null) {
      return false; // Default to inactive if no status is provided
    }
    return room.isActive === 1 || room.isActive === true || room.isActive === '1';
  };
  
  // Helper function to validate room object
  const isValidRoom = (room) => {
    return room && 
           room.room_type_category_id && 
           room.category_name && 
           typeof room.room_type_category_id === 'number';
  };
  
  const [roomTypes, setRoomTypes] = useState([]);
  const [pendingRoomtypeApprovals, setPendingRoomtypeApprovals] = useState([]);
  const [roomsListCurrency, setRoomsListCurrency] = useState('INR');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [amenitiesByRoom, setAmenitiesByRoom] = useState({});
  const [occupancyByRoom, setOccupancyByRoom] = useState({});
  const [amenitiesLoading, setAmenitiesLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalRoom, setModalRoom] = useState(null);
  const [modalSelected, setModalSelected] = useState({});
  const [savingAmenities, setSavingAmenities] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    room_type_id: '',
    guests_allowed: '',
    room_count: '',
    start_date: '',
    end_date: '',
    rates: {},
  });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');
  const [editDialog, setEditDialog] = useState({ open: false, room: null });
  const [editType, setEditType] = useState('');
  const [editForm, setEditForm] = useState({
    occupancy: '',
    room_count: '',
    rates: {},
    start_date: '',
    end_date: '',
  });
  const [editAmenities, setEditAmenities] = useState({});
  const [editAmenitiesOpen, setEditAmenitiesOpen] = useState(false);
  const [editDetailsLoading, setEditDetailsLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [addAmenities, setAddAmenities] = useState({});
  const { successToast, errorToast } = useCustomToast();
  const [addAmenitiesModalOpen, setAddAmenitiesModalOpen] = useState(false);
  const [editMenuAnchor, setEditMenuAnchor] = useState(null);
  const [editMenuRoom, setEditMenuRoom] = useState(null);
  const [renameDialog, setRenameDialog] = useState({ open: false, room: null, newName: '', selectedRoomType: null });
  const [renaming, setRenaming] = useState(false);
  const [availableRoomTypes, setAvailableRoomTypes] = useState([]);
  const [loadingRoomTypes, setLoadingRoomTypes] = useState(false);
  const [roomTypeOptions, setRoomTypeOptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  
  // New state for room type status management
  const [statusUpdateLoading, setStatusUpdateLoading] = useState({});
  const [statusUpdateDialog, setStatusUpdateDialog] = useState({ open: false, room: null, action: '' });
  
  // New state for amenities view more functionality
  const [expandedAmenities, setExpandedAmenities] = useState({});
  const [amenitiesToShow, setAmenitiesToShow] = useState({});
  const [hasAddedTwoYears, setHasAddedTwoYears] = useState(false);

  const token = getDecryptedToken();
  const hotelId = localStorage.getItem('selectedHotelId');
  const todayStr = dayjs().format('YYYY-MM-DD');

  /** Same render cycle में search को ताज़ा roomTypes चाहिए; effect deps सिर्फ id-signature पर रखें */
  const roomTypesRef = useRef(roomTypes);
  roomTypesRef.current = roomTypes;

  const roomTypesSignature = useMemo(
    () =>
      roomTypes
        .map((r) => String(r.room_type_category_id))
        .sort()
        .join(','),
    [roomTypes]
  );

  // Initialize amenities display
  useEffect(() => {
    const initialAmenitiesToShow = {};
    Object.keys(amenitiesByRoom).forEach(roomId => {
      const amenities = amenitiesByRoom[roomId] || [];
      initialAmenitiesToShow[roomId] = amenities.length > 6 ? 6 : amenities.length;
    });
    setAmenitiesToShow(initialAmenitiesToShow);
  }, [amenitiesByRoom]);

  const fetchAmenities = async (signal) => {
    if (!token || !hotelId) return;
    setAmenitiesLoading(true);
    try {
      const formData = new FormData();
      formData.append('hotel_id', hotelId);
      formData.append('token', token);
      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getHotelDetails',
        formData,
        signal ? { signal } : undefined
      );
      if (response.data.status && Array.isArray(response.data.amenities_by_category)) {
        const map = {};
        const occMap = {};
        response.data.amenities_by_category.forEach(cat => {
          map[cat.category_id] = cat.amenities || [];
          occMap[cat.category_id] = cat.occupancy || 1;
        });
        setAmenitiesByRoom(map);
        setOccupancyByRoom(occMap);
      }
    } catch (error) {
      if (axios.isCancel?.(error) || error.code === 'ERR_CANCELED') return;
      console.error('Error fetching amenities:', error);
    } finally {
      setAmenitiesLoading(false);
    }
  };

  /** Room list + hotel details: एक shared promise (Strict Mode पर भी एक ही HTTP जोड़ा) */
  useEffect(() => {
    if (!token || !hotelId) {
      setLoading(false);
      return;
    }

    let active = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      setAmenitiesLoading(true);
      try {
        const { response, hotelDetailsResponse } = await fetchRoomTypesPageDataOnce(token, hotelId);
        if (!active) return;

        if (response.data.status) {
          const rooms = Array.isArray(response.data.rooms_details) ? response.data.rooms_details : [];
          setRoomTypes(rooms);
          setPendingRoomtypeApprovals(
            Array.isArray(response.data.pending_roomtype_approvals)
              ? response.data.pending_roomtype_approvals
              : []
          );
          setRoomsListCurrency(response.data.currency || 'INR');

          if (rooms.length > 0 && hotelDetailsResponse?.data?.status &&
              Array.isArray(hotelDetailsResponse.data.amenities_by_category)) {
            const map = {};
            const occMap = {};
            hotelDetailsResponse.data.amenities_by_category.forEach(cat => {
              map[cat.category_id] = cat.amenities || [];
              occMap[cat.category_id] = cat.occupancy || 1;
            });
            setAmenitiesByRoom(map);
            setOccupancyByRoom(occMap);
          } else {
            setAmenitiesByRoom({});
            setOccupancyByRoom({});
          }
        } else {
          setError(response.data.message || 'Failed to fetch room types');
        }
      } catch (error) {
        if (!active) return;
        console.error('Error fetching room types:', error);
        setError('Failed to fetch room types');
      } finally {
        if (active) {
          setAmenitiesLoading(false);
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [token, hotelId]);

  const searchRoomTypes = useCallback(async (query) => {
    if (!token || !hotelId) {
      setRoomTypeOptions([]);
      setAvailableRoomTypes([]);
      return;
    }

    const qTrim = String(query || '').trim();
    const normalizedQuery = normalizeRoomSearchText(qTrim);
    const tokens = normalizedQuery.split(/\s+/).filter(Boolean);
    /** First word keeps API results broad; client filter handles paste / full phrase */
    const apiSearch = tokens.length === 0 ? '' : tokens[0];

    const rts = roomTypesRef.current;

    setIsSearching(true);
    try {
      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getAvailableRoomTypes',
        { 
          hotel_id: Number(hotelId),
          search: apiSearch
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      
      if (response.data.status && Array.isArray(response.data.data)) {
        const raw = response.data.data;
        /** पूरे सबस्ट्रिंग की जगह टोकन-AND (क्रम मुक्त); API की चौड़ी सूची पर क्लाइंट फ़िल्टर */
        const tokenFiltered = tokens.length
          ? raw.filter((opt) => roomOptionMatchesQuery(opt, qTrim))
          : raw;
        const capped = tokenFiltered.slice(0, 80);

        setRoomTypeOptions(capped.length ? capped : raw.slice(0, 80));
        const addedRoomTypeIds = rts.map(rt => parseInt(rt.room_type_category_id, 10));
        const base = capped.length ? capped : raw.slice(0, 80);
        const available = base.filter(opt => !addedRoomTypeIds.includes(opt.id));
        setAvailableRoomTypes(available);
      } else {
        setRoomTypeOptions([]);
        setAvailableRoomTypes([]);
      }
    } catch (error) {
      console.error('Error searching room types:', error);
      setRoomTypeOptions([]);
      setAvailableRoomTypes([]);
    } finally {
      setIsSearching(false);
    }
  }, [token, hotelId]);

  /** getAvailableRoomTypes सिर्फ़ जब Add मॉडल खुला हो — पेज लोड पर बेकार कॉल नहीं */
  useEffect(() => {
    if (!addModalOpen || !token || !hotelId) return;

    let cancelled = false;
    const delay = searchQuery.trim().length >= 1 ? 300 : 0;
    const timeoutId = setTimeout(() => {
      if (cancelled) return;
      searchRoomTypes(searchQuery);
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [searchQuery, token, hotelId, roomTypesSignature, searchRoomTypes, addModalOpen]);

  const handleStatusUpdate = async () => {
    const { room, action } = statusUpdateDialog;
    
    // Early return if no room or action
    if (!isValidRoom(room) || !action) {
      errorToast('Invalid room type or action. Please try again.');
      return;
    }
    
    const roomId = room.room_type_category_id;
    
    // Check if token is valid
    if (!token) {
      errorToast('Authentication token is missing. Please log in again.');
      return;
    }
    
    // Check if hotel ID is valid
    if (!hotelId) {
      errorToast('Hotel ID is missing. Please select a hotel first.');
      return;
    }
    
    setStatusUpdateLoading(prev => ({ ...prev, [roomId]: true }));
    
    try {
      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/updateRoomTypeStatus',
        {
          hotel_id: parseInt(hotelId),
          roomtype_id: roomId,
          token: token,
          status: action === 'activate' ? 'active' : 'inactive'
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      
      if (response.data.status) {
        const actionText = action === 'activate' ? 'activated' : 'deactivated';
        const newStatus = action === 'activate' ? 'Active' : 'Inactive';
        successToast(`Room type "${room.category_name.replace(/_/g, ' ')}" ${actionText} successfully. Status: ${newStatus}`);
        
        // Update the room type status in the local state
        setRoomTypes(prev => prev.map(rt => 
          rt.room_type_category_id === roomId 
            ? { ...rt, isActive: action === 'activate' ? 1 : 0 }
            : rt
        ));
        
        setStatusUpdateDialog({ open: false, room: null, action: '' });
      } else {
        errorToast(response.data.message || `Failed to ${action} room type. Please try again.`);
      }
    } catch (error) {
      console.error(`Error ${action}ing room type:`, error);
      if (error.response?.data?.message) {
        errorToast(`API Error: ${error.response.data.message}`);
      } else if (error.response?.status === 400) {
        errorToast('Invalid request. Please check the room type details and try again.');
      } else if (error.response?.status === 401) {
        errorToast('Authentication failed. Please log in again.');
      } else if (error.response?.status === 500) {
        errorToast('Server error. Please try again later.');
      } else {
        errorToast(`Failed to ${action} room type. Please check your connection and try again.`);
      }
    } finally {
      setStatusUpdateLoading(prev => ({ ...prev, [roomId]: false }));
    }
  };

  const handleOpenStatusDialog = (room, action) => {
    if (!isValidRoom(room) || !action) {
      errorToast('Invalid room type or action. Please try again.');
      return;
    }
    
    setStatusUpdateDialog({ open: true, room, action });
  };

  const handleEditAmenities = (room) => {
    if (!isValidRoom(room)) {
      errorToast('Invalid room type. Please try again.');
      return;
    }
    
    const selected = {};
    const current = amenitiesByRoom[room.room_type_category_id] || [];
    amenitiesSections.forEach(section => {
      selected[section.key] = section.options.filter(opt => current.includes(opt));
    });
    setModalSelected(selected);
    setModalRoom(room);
    setModalOpen(true);
  };

  const handleSaveAmenities = async () => {
    if (!modalRoom) return;
    setSavingAmenities(true);
    try {
      const allSelected = Object.values(modalSelected).flat();
      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/update.php?function=updateRoomTypeAmenities',
        {
          hotel_id: hotelId,
          room_type_category_id: modalRoom.room_type_category_id,
          amenities: allSelected.join(', '),
          token
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.data.status) {
        setAmenitiesByRoom(prev => ({ ...prev, [modalRoom.room_type_category_id]: allSelected }));
        setModalOpen(false);
        successToast('Amenities updated successfully');
      } else {
        errorToast(response.data.message || 'Failed to update amenities');
      }
    } catch (error) {
      console.error('Error updating amenities:', error);
      errorToast('Failed to update amenities');
    } finally {
      setSavingAmenities(false);
    }
  };

  const handleOpenAddModal = () => {
    setAddModalOpen(true);
    setAddForm({
      room_type_id: '',
      guests_allowed: '',
      room_count: '',
      start_date: todayStr,
      end_date: '',
      rates: {},
    });
    setAddError('');
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
    setAddForm({
      room_type_id: '',
      guests_allowed: '',
      room_count: '',
      start_date: '',
      end_date: '',
      rates: {},
    });
    setAddError('');
    setHasAddedTwoYears(false);
  };

  const handleAddFormChange = (field, value) => {
    setAddForm(prev => ({ ...prev, [field]: value }));
    if (field === 'guests_allowed') {
      // Reset rates if guests_allowed changes
      const n = parseInt(value) || 0;
      const newRates = {};
      for (let i = 1; i <= n; i++) newRates[`${i}guest`] = '';
      setAddForm(prev => ({ ...prev, rates: newRates }));
    }
  };

  const handleRateChange = (guestKey, value) => {
    setAddForm(prev => ({
      ...prev,
      rates: { ...prev.rates, [guestKey]: value }
    }));
  };

  const handleAddAmenityChange = (categoryKey, option) => {
    setAddAmenities(prev => {
      const current = prev[categoryKey] || [];
      if (current.includes(option)) {
        return { ...prev, [categoryKey]: current.filter(a => a !== option) };
      } else {
        return { ...prev, [categoryKey]: [...current, option] };
      }
    });
  };

  const handleAddTwoYears = () => {
    if (!hasAddedTwoYears && addForm.start_date) {
      const startDate = new Date(addForm.start_date);
      const endDate = new Date(startDate);
      endDate.setFullYear(endDate.getFullYear() + 2);
      
      const endDateStr = endDate.toISOString().split('T')[0];
      setAddForm(prev => ({ ...prev, end_date: endDateStr }));
      setHasAddedTwoYears(true);
    }
  };

  const buildEmptyRates = (occupancy, previous = {}) => {
    const occ = Math.max(0, Math.min(20, parseInt(occupancy, 10) || 0));
    const rates = {};
    for (let i = 1; i <= occ; i += 1) {
      rates[`${i}guest`] = previous[`${i}guest`] != null ? String(previous[`${i}guest`]) : '';
    }
    return rates;
  };

  const amenitiesSelectionFromList = (list) => {
    const current = Array.isArray(list) ? list : [];
    const selected = {};
    amenitiesSections.forEach((section) => {
      selected[section.key] = section.options.filter((opt) => current.includes(opt));
    });
    return selected;
  };

  const handleOpenEditDialog = async (room) => {
    if (!isValidRoom(room)) {
      errorToast('Invalid room type. Please try again.');
      return;
    }

    const occupancy = occupancyByRoom[room.room_type_category_id] || 1;
    const endDate = dayjs().add(1, 'year').format('YYYY-MM-DD');
    setEditDialog({ open: true, room });
    setEditAmenities(amenitiesSelectionFromList(amenitiesByRoom[room.room_type_category_id]));
    setEditError('');
    setEditForm({
      occupancy: String(occupancy),
      room_count: '',
      rates: buildEmptyRates(occupancy),
      start_date: todayStr,
      end_date: endDate,
    });
    setEditDetailsLoading(true);

    try {
      const formData = new FormData();
      formData.append('hotel_id', hotelId);
      formData.append('token', token);
      formData.append('last_date', todayStr);
      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getHotelDetails',
        formData
      );
      const inventory = Array.isArray(response.data?.inventory) ? response.data.inventory : [];
      const row = inventory.find(
        (item) => Number(item.roomtype_id) === Number(room.room_type_category_id)
      );
      let rateData = {};
      if (row?.rate) {
        rateData = typeof row.rate === 'string' ? JSON.parse(row.rate) : row.rate;
      }
      const occFromDetails = response.data?.amenities_by_category?.find(
        (cat) => Number(cat.category_id) === Number(room.room_type_category_id)
      )?.occupancy;
      const occ = Number(occFromDetails) || Number(occupancy) || Object.keys(rateData).length || 1;
      setEditForm((prev) => ({
        ...prev,
        occupancy: String(occ),
        room_count: row?.count != null ? String(row.count) : '',
        rates: buildEmptyRates(occ, rateData),
      }));
    } catch (error) {
      console.error('Error loading room details:', error);
    } finally {
      setEditDetailsLoading(false);
    }
  };

  const handleEditFormChange = (field, value) => {
    if (field === 'occupancy') {
      const digits = String(value).replace(/\D/g, '').slice(0, 2);
      setEditForm((prev) => ({
        ...prev,
        occupancy: digits,
        rates: buildEmptyRates(digits, prev.rates),
      }));
      return;
    }
    if (field === 'room_count') {
      setEditForm((prev) => ({
        ...prev,
        room_count: String(value).replace(/\D/g, '').slice(0, 3),
      }));
      return;
    }
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditRateChange = (guestKey, value) => {
    setEditForm((prev) => ({
      ...prev,
      rates: { ...prev.rates, [guestKey]: String(value).replace(/[^\d.]/g, '') },
    }));
  };

  const handleSaveRoomEdit = async () => {
    const room = editDialog.room;
    if (!isValidRoom(room)) {
      setEditError('Invalid room type. Please try again.');
      return;
    }

    const occupancy = parseInt(editForm.occupancy, 10);
    const roomCount = parseInt(editForm.room_count, 10);
    if (!occupancy || occupancy < 1 || occupancy > 20) {
      setEditError('Occupancy must be between 1 and 20');
      return;
    }
    if (!roomCount || roomCount < 1 || roomCount > 100) {
      setEditError('Room count must be between 1 and 100');
      return;
    }
    if (!editForm.start_date || !editForm.end_date || editForm.end_date < editForm.start_date) {
      setEditError('Choose a valid start and end date');
      return;
    }

    const rateObject = {};
    let previousPrice = 0;
    for (let i = 1; i <= occupancy; i += 1) {
      const price = Number(editForm.rates[`${i}guest`]);
      if (!Number.isFinite(price) || price <= 0) {
        setEditError(`Enter a rate for ${i} guest${i > 1 ? 's' : ''}`);
        return;
      }
      if (i > 1 && price < previousPrice) {
        setEditError(`The ${i}-guest rate must be at least the ${i - 1}-guest rate`);
        return;
      }
      rateObject[`${i}guest`] = price;
      previousPrice = price;
    }

    const roomTypeId = room.room_type_category_id;
    const amenities = Object.values(editAmenities).flat();
    setEditLoading(true);
    setEditError('');

    try {
      const occupancyResponse = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/updateOccupancyAndRate',
        {
          token,
          hotel_id: parseInt(hotelId, 10),
          room_type_id: roomTypeId,
          occupancy,
          rate: rateObject,
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (!occupancyResponse.data?.status) {
        throw new Error(occupancyResponse.data?.message || 'Failed to update occupancy and rates');
      }

      const countResponse = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/setCount',
        {
          hotel_id: parseInt(hotelId, 10),
          token,
          start_date: editForm.start_date,
          end_date: editForm.end_date,
          room_types: [{ roomtype_id: String(roomTypeId), count: roomCount }],
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (!countResponse.data?.status) {
        throw new Error(countResponse.data?.message || 'Failed to update room count');
      }

      // setBulkRates follows setCount; the inventory API needs a short gap between them.
      await new Promise((resolve) => setTimeout(resolve, 4000));

      const ratesResponse = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/setBulkRates',
        {
          hotel_id: parseInt(hotelId, 10),
          token,
          start_date: editForm.start_date,
          end_date: editForm.end_date,
          room_types: [{
            roomtype_id: String(roomTypeId),
            rate: JSON.stringify(rateObject),
            occupancy,
          }],
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (!ratesResponse.data?.status) {
        throw new Error(ratesResponse.data?.message || 'Failed to update rates');
      }

      const amenitiesResponse = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/update.php?function=updateRoomTypeAmenities',
        {
          hotel_id: hotelId,
          room_type_category_id: roomTypeId,
          amenities: amenities.join(', '),
          token,
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (!amenitiesResponse.data?.status) {
        throw new Error(amenitiesResponse.data?.message || 'Failed to update amenities');
      }

      setOccupancyByRoom((prev) => ({ ...prev, [roomTypeId]: occupancy }));
      setAmenitiesByRoom((prev) => ({ ...prev, [roomTypeId]: amenities }));
      successToast('Room updated');
      setEditDialog({ open: false, room: null });
    } catch (error) {
      console.error('Error updating room:', error);
      setEditError(error.response?.data?.message || error.message || 'Failed to update room');
    } finally {
      setEditLoading(false);
    }
  };

  const handleAddRoomType = async () => {
    setAddError('');
    const roomTypeIdNum = Number(addForm.room_type_id);
    
    // Basic validation
    if (!roomTypeIdNum || !addForm.guests_allowed || !addForm.room_count || !addForm.start_date || !addForm.end_date) {
      setAddError('All fields are required.');
      return;
    }
    
    // Check if rates are provided
    if (Object.values(addForm.rates).some(v => !v || v === '')) {
      setAddError('Please enter all rates.');
      return;
    }
    
    setAddLoading(true);
    try {
      // Prepare payload exactly as expected by API
      const allSelectedAmenities = Object.values(addAmenities).flat();
      const payload = {
        token: token,
        hotel_id: parseInt(hotelId),
        room_type_id: roomTypeIdNum,
        occupancy: parseInt(addForm.guests_allowed),
        amenities: allSelectedAmenities.join(', '),
        rate: JSON.stringify(
          Object.fromEntries(
            Object.entries(addForm.rates).map(([k, v]) => [k, Number(v)])
          )
        ),
        start_date: addForm.start_date,
        end_date: addForm.end_date,
        room_count: parseInt(addForm.room_count),
      };
      
      console.log('Add Room Type Payload:', payload);
      
      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php?action=addNewRoomApprovalRequest',
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );
      
      console.log('API Response:', response.data);
      
      if (response.data.status) {
        setAddModalOpen(false);
        setAddAmenities({});
        setAddForm({
          room_type_id: '',
          guests_allowed: '',
          room_count: '',
          start_date: todayStr,
          end_date: '',
          rates: {},
        });
        successToast('Room type added successfully');
        
        // Refresh room types
        const refetch = await axios.post(
          'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getAllRoomTypes',
          { token, hotel_id: Number(hotelId) },
          { headers: { 'Content-Type': 'application/json' } }
        );
        if (refetch.data.status) {
          const rooms = Array.isArray(refetch.data.rooms_details) ? refetch.data.rooms_details : [];
          setRoomTypes(rooms);
          setPendingRoomtypeApprovals(
            Array.isArray(refetch.data.pending_roomtype_approvals)
              ? refetch.data.pending_roomtype_approvals
              : []
          );
          if (refetch.data.currency) setRoomsListCurrency(refetch.data.currency);
          if (rooms.length > 0) {
            await fetchAmenities();
          } else {
            setAmenitiesByRoom({});
            setOccupancyByRoom({});
          }
        }
      } else {
        setAddError(response.data.message || 'Failed to add room type');
        errorToast(response.data.message || 'Failed to add room type');
      }
    } catch (error) {
      console.error('Error adding room type:', error);
      console.error('Error response:', error.response?.data);
      setAddError(error.response?.data?.message || 'Error adding room type');
      errorToast(error.response?.data?.message || 'Error adding room type');
    } finally {
      setAddLoading(false);
    }
  };

  const handleOpenEditMenu = (event, room) => {
    if (!isValidRoom(room)) {
      errorToast('Invalid room type. Please try again.');
      return;
    }
    
    setEditMenuAnchor(event.currentTarget);
    setEditMenuRoom(room);
  };

  const handleCloseEditMenu = () => {
    setEditMenuAnchor(null);
    setEditMenuRoom(null);
  };

  const fetchAvailableRoomTypes = async () => {
    setLoadingRoomTypes(true);
    try {
      const response = await axios.get(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getAvailableRoomTypes'
      );
      
      if (response.data.status && response.data.data) {
        setAvailableRoomTypes(response.data.data);
      } else {
        errorToast('Failed to fetch available room types');
      }
    } catch (error) {
      console.error('Error fetching available room types:', error);
      errorToast('Failed to fetch available room types');
    } finally {
      setLoadingRoomTypes(false);
    }
  };

  const handleOpenRenameDialog = (room) => {
    setRenameDialog({
      open: true,
      room: room,
      newName: room.category_name || '',
      selectedRoomType: null
    });
    fetchAvailableRoomTypes();
  };

  const handleCloseRenameDialog = () => {
    setRenameDialog({ open: false, room: null, newName: '', selectedRoomType: null });
  };

  const handleRenameRoomType = async () => {
    if (!renameDialog.room || !renameDialog.selectedRoomType) {
      errorToast('Please select a new room type');
      return;
    }

    if (renameDialog.selectedRoomType.id === renameDialog.room.room_type_category_id) {
      errorToast('New room type must be different from current room type');
      return;
    }

    setRenaming(true);
    try {
      const token = getDecryptedToken();
      if (!token) {
        errorToast('Please log in to change room type');
        return;
      }

      const hotelId = localStorage.getItem('selectedHotelId');
      if (!hotelId) {
        errorToast('No hotel selected');
        return;
      }

      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/changeRoomType',
        {
          hotel_id: hotelId,
          previous_room_type_category_id: renameDialog.room.room_type_category_id,
          new_room_type_category_id: renameDialog.selectedRoomType.id,
          token
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.data.status) {
        successToast('Room type changed successfully');
        
        // Update the room type in the local state
        setRoomTypes(prev => prev.map(room => 
          room.room_type_category_id === renameDialog.room.room_type_category_id
            ? { 
                ...room, 
                category_name: renameDialog.selectedRoomType.category_name,
                room_type_category_id: renameDialog.selectedRoomType.id
              }
            : room
        ));
        
        handleCloseRenameDialog();
      } else {
        errorToast(response.data.message || 'Failed to change room type');
      }
    } catch (error) {
      console.error('Error changing room type:', error);
      errorToast('Failed to change room type');
    } finally {
      setRenaming(false);
    }
  };

  const handleEditMenuAction = (action) => {
    if (!editMenuRoom || !isValidRoom(editMenuRoom)) {
      errorToast('No room type selected. Please try again.');
      return;
    }
    
    if (action === 'edit') {
      handleOpenEditDialog(editMenuRoom);
    } else if (action === 'rename') {
      handleOpenRenameDialog(editMenuRoom);
    } else if (action === 'amenities') {
      handleEditAmenities(editMenuRoom);
    } else if (action === 'activate' || action === 'deactivate') {
      handleOpenStatusDialog(editMenuRoom, action);
    }
    handleCloseEditMenu();
  };

  // Handle amenities view more/less toggle
  const handleToggleAmenities = (roomId) => {
    setExpandedAmenities(prev => ({
      ...prev,
      [roomId]: !prev[roomId]
    }));
  };

  // Get amenities to display based on expanded state
  const getAmenitiesToDisplay = (roomId) => {
    const amenities = amenitiesByRoom[roomId] || [];
    const isExpanded = expandedAmenities[roomId];
    const maxToShow = isMobile ? 4 : 6;
    
    if (amenities.length <= maxToShow) {
      return amenities;
    }
    
    return isExpanded ? amenities : amenities.slice(0, maxToShow);
  };

  const formatMoneyShort = (amount, currencyCode) => {
    const n = Number(amount);
    if (Number.isNaN(n)) return String(amount ?? '');
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode || 'INR',
        maximumFractionDigits: n >= 1000 ? 0 : 2,
      }).format(n);
    } catch {
      return `${currencyCode || ''} ${n}`.trim();
    }
  };

  const formatPendingGuestRateRows = (rate, currencyCode) => {
    if (!rate || typeof rate !== 'object') return [];
    return Object.entries(rate).map(([key, val]) => {
      const m = String(key).match(/(\d+)\s*guest/i);
      const guests = m ? m[1] : key.replace(/_/g, ' ');
      const label = m ? `${guests} guest${guests === '1' ? '' : 's'}` : key.replace(/_/g, ' ');
      return { label, value: formatMoneyShort(val, currencyCode) };
    });
  };

  const pendingAmenityTokens = (amenitiesStr) => {
    if (!amenitiesStr || typeof amenitiesStr !== 'string') return [];
    return amenitiesStr
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  };

  // Loading skeleton component
  const RoomTypeSkeleton = () => (
    <Card elevation={2} sx={{ borderRadius: 3, height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Skeleton variant="circular" width={56} height={56} />
          <Skeleton variant="text" width="80%" height={32} />
          <Skeleton variant="text" width="60%" height={24} />
          <Box sx={{ width: '100%', mt: 2 }}>
            <Skeleton variant="text" width="40%" height={20} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {[1, 2, 3].map(i => (
                <Skeleton key={i} variant="rounded" width={60} height={24} />
              ))}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ 
      p: { xs: 2, sm: 3, md: 4 },
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
    }}>
      {/* Header Section */}
      <Box sx={{ 
        mb: { xs: 3, md: 4 },
        background: 'white',
        borderRadius: 3,
        p: { xs: 2, sm: 3 },
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(0, 0, 0, 0.05)'
      }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: { xs: 'stretch', sm: 'center' }, 
          gap: 2 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 },
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #FF7700 0%, #FFA040 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(255, 119, 0, 0.3)'
            }}>
              <HotelIcon sx={{ color: 'white', fontSize: { xs: 20, sm: 24 } }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ 
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                color: '#1e293b',
                mb: 0.5
              }}>
                Room Types
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                Manage your hotel's room categories and amenities
              </Typography>
            </Box>
          </Box>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ 
              background: 'linear-gradient(90deg, #FF7700 0%, #FFA040 100%)', 
              color: 'white', 
              fontWeight: 700, 
              borderRadius: 2.5, 
              boxShadow: '0 4px 12px rgba(255, 119, 0, 0.3)',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              px: { xs: 2, sm: 3 },
              py: { xs: 1.5, sm: 2 },
              minWidth: { xs: '100%', sm: 'auto' },
              '&:hover': { 
                background: 'linear-gradient(90deg, #FF7700 0%, #FF7700 100%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 16px rgba(255, 119, 0, 0.4)'
              },
              transition: 'all 0.2s ease'
            }}
            onClick={handleOpenAddModal}
          >
            Add Room Type
          </Button>
        </Box>
      </Box>

      {/* Content Section */}
      {loading ? (
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
              <RoomTypeSkeleton />
            </Grid>
          ))}
        </Grid>
      ) : error ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '40vh',
          textAlign: 'center'
        }}>
          <Typography variant="h6" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Button 
            variant="outlined" 
            onClick={() => window.location.reload()}
            sx={{ borderRadius: 2 }}
          >
            Try Again
          </Button>
        </Box>
      ) : roomTypes.filter(isValidRoom).length === 0 && pendingRoomtypeApprovals.length === 0 ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '40vh',
          textAlign: 'center',
          background: 'white',
          borderRadius: 3,
          p: 4,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
        }}>
          <HotelIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            No Room Types Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Get started by adding your first room type to showcase your hotel's offerings.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddModal}
            sx={{ 
              background: 'linear-gradient(90deg, #FF7700 0%, #FFA040 100%)',
              borderRadius: 2.5,
              px: 3,
              py: 1.5
            }}
          >
            Add Your First Room Type
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {pendingRoomtypeApprovals.length > 0 && (
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid rgba(245, 158, 11, 0.35)',
                  background: 'linear-gradient(90deg, rgba(245, 158, 11, 0.08) 0%, rgba(255, 255, 255, 0.9) 100%)',
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    bgcolor: 'rgba(245, 158, 11, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <HourglassEmptyIcon sx={{ color: 'warning.dark', fontSize: 26 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                    Pending approval
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
                    These room types are submitted for review. They are not bookable until approved. You can still manage live room types below.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          )}
          {pendingRoomtypeApprovals.map((pending, pIdx) => {
            const details = pending.room_details || {};
            const reqId = details.approval_request_id ?? `idx-${pIdx}`;
            const name = (pending.category_name || details.category_name || 'Room type').replace(/_/g, ' ');
            const occ = details.occupancy ?? pending.occupancy;
            const roomCount = details.room_count ?? pending.room_count;
            const startD = details.start_date;
            const endD = details.end_date;
            const amenityList = pendingAmenityTokens(details.amenities || pending.amenities);
            const rateRows = formatPendingGuestRateRows(details.rate, roomsListCurrency);
            const roomCreated = pending.room_created;

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`pending-${reqId}`}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 3,
                    minHeight: '280px',
                    position: 'relative',
                    overflow: 'visible',
                    background: 'linear-gradient(180deg, rgba(255, 251, 235, 0.95) 0%, #fff 48%)',
                    border: '2px dashed rgba(245, 158, 11, 0.45)',
                    boxShadow: '0 2px 12px rgba(245, 158, 11, 0.12)',
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 8px 24px rgba(245, 158, 11, 0.18)',
                      borderColor: 'rgba(245, 158, 11, 0.65)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      zIndex: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 0.75,
                    }}
                  >
                    <Chip
                      icon={<HourglassEmptyIcon sx={{ fontSize: '16px !important', color: 'warning.dark' }} />}
                      label="Pending"
                      size="small"
                      sx={{
                        fontWeight: 700,
                        bgcolor: 'rgba(245, 158, 11, 0.18)',
                        color: 'warning.dark',
                        border: '1px solid rgba(217, 119, 6, 0.45)',
                        '& .MuiChip-icon': { color: 'warning.dark' },
                      }}
                    />
                    {roomCreated === 'no' && (
                      <Chip
                        label="Not live"
                        size="small"
                        variant="outlined"
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          borderColor: 'rgba(148, 163, 184, 0.6)',
                          color: 'text.secondary',
                        }}
                      />
                    )}
                  </Box>

                  <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', pt: 2.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, mt: 1 }}>
                      <Box
                        sx={{
                          width: { xs: 48, sm: 56 },
                          height: { xs: 48, sm: 56 },
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 14px rgba(245, 158, 11, 0.35)',
                        }}
                      >
                        <HotelIcon sx={{ color: 'white', fontSize: { xs: 24, sm: 28 } }} />
                      </Box>
                    </Box>

                    <Typography
                      variant="h6"
                      fontWeight={700}
                      align="center"
                      sx={{
                        mb: 2,
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                        color: '#1e293b',
                        lineHeight: 1.35,
                        pr: 1,
                        pl: 1,
                      }}
                    >
                      {name}
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1, mb: 2 }}>
                      {occ != null && occ !== '' && (
                        <Chip
                          icon={<GroupIcon sx={{ color: 'warning.dark', fontSize: 16 }} />}
                          label={`${occ} Guest${Number(occ) > 1 ? 's' : ''}`}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(245, 158, 11, 0.12)',
                            color: 'warning.dark',
                            fontWeight: 600,
                            border: '1px solid rgba(245, 158, 11, 0.25)',
                          }}
                        />
                      )}
                      {roomCount != null && roomCount !== '' && (
                        <Chip
                          icon={<MeetingRoomIcon sx={{ color: 'warning.dark', fontSize: 16 }} />}
                          label={`${roomCount} room${Number(roomCount) > 1 ? 's' : ''}`}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(245, 158, 11, 0.12)',
                            color: 'warning.dark',
                            fontWeight: 600,
                            border: '1px solid rgba(245, 158, 11, 0.25)',
                          }}
                        />
                      )}
                    </Box>

                    {(startD || endD) && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.75,
                          mb: 2,
                          flexWrap: 'wrap',
                        }}
                      >
                        <CalendarMonthIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          {startD && endD ? `${startD} → ${endD}` : startD || endD}
                        </Typography>
                      </Box>
                    )}

                    {rateRows.length > 0 && (
                      <Box
                        sx={{
                          mb: 2,
                          p: 1.25,
                          borderRadius: 2,
                          bgcolor: 'rgba(15, 23, 42, 0.04)',
                          border: '1px solid rgba(15, 23, 42, 0.06)',
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={700}
                          sx={{ display: 'block', mb: 0.75, letterSpacing: 0.3 }}
                        >
                          Requested rates
                        </Typography>
                        {rateRows.map((row) => (
                          <Box
                            key={row.label}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              gap: 1,
                              py: 0.25,
                            }}
                          >
                            <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                              {row.label}
                            </Typography>
                            <Typography variant="body2" fontWeight={700} color="text.primary">
                              {row.value}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 'auto' }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{ fontWeight: 600, mb: 1, textAlign: 'center' }}
                      >
                        Amenities (request)
                      </Typography>
                      {amenityList.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ fontStyle: 'italic' }}>
                          —
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                          {amenityList.map((a) => (
                            <Chip
                              key={a}
                              label={a}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontWeight: 500,
                                fontSize: '0.72rem',
                                borderColor: 'rgba(245, 158, 11, 0.4)',
                                color: 'warning.dark',
                                bgcolor: 'rgba(255, 255, 255, 0.8)',
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
          {roomTypes
            .filter(isValidRoom) // Filter out invalid rooms using the validation function
            .map(room => {
            const amenities = isValidRoom(room) ? getAmenitiesToDisplay(room.room_type_category_id) : [];
            const totalAmenities = amenitiesByRoom[room.room_type_category_id]?.length || 0;
            const isExpanded = expandedAmenities[room.room_type_category_id];
            const hasMoreAmenities = totalAmenities > (isMobile ? 4 : 6);
            
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={room.room_type_category_id}>
                <Card
                  elevation={2}
                  sx={{
                    borderRadius: 3,
                    minHeight: '280px',
                    background: 'white',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'visible',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                      borderColor: 'rgba(255, 119, 0, 0.3)'
                    },
                    ...(room && room.room_type_category_id && statusUpdateLoading[room.room_type_category_id] && {
                      animation: 'pulse 1.5s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%': {
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                        },
                        '50%': {
                          boxShadow: '0 4px 20px rgba(255, 119, 0, 0.3)'
                        },
                        '100%': {
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                        }
                      }
                    })
                  }}
                >
                  {/* Action Menu Button */}
                  <IconButton
                    aria-label="room-actions"
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      background: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(8px)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      zIndex: 2,
                      '&:hover': {
                        background: 'rgba(255, 119, 0, 0.1)',
                        color: '#FF7700'
                      }
                    }}
                    onClick={e => handleOpenEditMenu(e, room)}
                    disabled={room && room.room_type_category_id && statusUpdateLoading[room.room_type_category_id]}
                  >
                    <MoreVertIcon />
                  </IconButton>

                  {/* Loading Overlay */}
                  {room && room.room_type_category_id && statusUpdateLoading[room.room_type_category_id] && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(255, 255, 255, 0.8)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 3,
                        borderRadius: 3
                      }}
                    >
                      <CircularProgress size={40} sx={{ color: '#FF7700' }} />
                    </Box>
                  )}

                  <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
                    {/* Room Icon */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      mb: 2 
                    }}>
                      <Box sx={{
                        width: { xs: 48, sm: 56 },
                        height: { xs: 48, sm: 56 },
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #FF7700 0%, #FFA040 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(255, 119, 0, 0.3)'
                      }}>
                        <HotelIcon sx={{ color: 'white', fontSize: { xs: 24, sm: 28 } }} />
                      </Box>
                    </Box>

                    {/* Room Name */}
                    <Typography 
                      variant="h6" 
                      fontWeight={700} 
                      align="center" 
                      sx={{ 
                        mb: 2,
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                        color: '#1e293b',
                        lineHeight: 1.3
                      }}
                    >
                      {room.category_name.replace(/_/g, ' ')}
                    </Typography>

                    {/* Guest Count Badge */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                      <Chip
                        icon={<GroupIcon sx={{ color: '#FF7700', fontSize: 16 }} />}
                        label={`${occupancyByRoom[room.room_type_category_id] || 1} Guest${(occupancyByRoom[room.room_type_category_id] || 1) > 1 ? 's' : ''}`}
                        size="small"
                        sx={{ 
                          backgroundColor: 'rgba(255, 119, 0, 0.1)', 
                          color: '#FF7700', 
                          fontWeight: 600,
                          border: '1px solid rgba(255, 119, 0, 0.2)'
                        }}
                      />
                    </Box>

                    {/* Status Badge */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <Tooltip 
                        title={isRoomActive(room) ? 
                          'This room type is currently active and available for bookings' : 
                          'This room type is currently inactive and unavailable for bookings'
                        }
                        arrow
                      >
                        <Chip
                          icon={isRoomActive(room) ? 
                            <PowerSettingsNewIcon sx={{ color: 'success.main', fontSize: 16 }} /> : 
                            <PowerOffIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
                          }
                          label={isRoomActive(room) ? 'Active' : 'Inactive'}
                          size="small"
                          sx={{ 
                            backgroundColor: isRoomActive(room) ? 'rgba(76, 175, 80, 0.1)' : 'rgba(158, 158, 158, 0.1)', 
                            color: isRoomActive(room) ? 'success.main' : 'text.secondary', 
                            fontWeight: 600,
                            border: `1px solid ${isRoomActive(room) ? 'rgba(76, 175, 80, 0.2)' : 'rgba(158, 158, 158, 0.2)'}`,
                            '& .MuiChip-icon': {
                              color: isRoomActive(room) ? 'success.main' : 'text.secondary'
                            },
                            transition: 'all 0.3s ease-in-out',
                            transform: room && room.room_type_category_id && statusUpdateLoading[room.room_type_category_id] ? 'scale(0.95)' : 'scale(1)',
                            opacity: room && room.room_type_category_id && statusUpdateLoading[room.room_type_category_id] ? 0.7 : 1
                          }}
                        />
                      </Tooltip>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenEditDialog(room)}
                        sx={{
                          borderRadius: 2,
                          borderColor: '#FF7700',
                          color: '#FF7700',
                          fontWeight: 700,
                          textTransform: 'none',
                          '&:hover': {
                            borderColor: '#FF7700',
                            backgroundColor: 'rgba(255, 119, 0, 0.08)',
                          },
                        }}
                      >
                        Edit
                      </Button>
                    </Box>

                    {/* Amenities Section */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 'auto' }}>
                      <Typography 
                        variant="subtitle2" 
                        color="text.secondary" 
                        sx={{ 
                          fontWeight: 600, 
                          mb: 1.5,
                          textAlign: 'center'
                        }}
                      >
                        Amenities
                      </Typography>
                      
                      {amenitiesLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                          <CircularProgress size={20} sx={{ color: '#FF7700' }} />
                        </Box>
                      ) : (
                        <Box>
                          {totalAmenities === 0 ? (
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              align="center"
                              sx={{ fontStyle: 'italic' }}
                            >
                              No amenities added
                            </Typography>
                          ) : (
                            <>
                              <Box sx={{ 
                                display: 'flex', 
                                flexWrap: 'wrap', 
                                gap: 0.5, 
                                justifyContent: 'center',
                                mb: hasMoreAmenities ? 1 : 0
                              }}>
                                {amenities.map(amenity => (
                                  <Chip 
                                    key={amenity} 
                                    label={amenity} 
                                    size="small" 
                                    variant="outlined"
                                    sx={{ 
                                      fontWeight: 500, 
                                      fontSize: '0.75rem',
                                      borderColor: 'rgba(255, 119, 0, 0.3)',
                                      color: '#FF7700',
                                      backgroundColor: 'rgba(255, 119, 0, 0.05)',
                                      '&:hover': {
                                        backgroundColor: 'rgba(255, 119, 0, 0.1)'
                                      }
                                    }} 
                                  />
                                ))}
                              </Box>
                              
                              {/* View More/Less Button */}
                              {hasMoreAmenities && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                  <Button
                                    size="small"
                                    onClick={() => isValidRoom(room) && handleToggleAmenities(room.room_type_category_id)}
                                    sx={{
                                      color: '#FF7700',
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      textTransform: 'none',
                                      '&:hover': {
                                        backgroundColor: 'rgba(255, 119, 0, 0.1)'
                                      }
                                    }}
                                    endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                  >
                                    {isExpanded ? 'Show Less' : `View ${totalAmenities - (isMobile ? 4 : 6)} More`}
                                  </Button>
                                </Box>
                              )}
                            </>
                          )}
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={editMenuAnchor}
        open={Boolean(editMenuAnchor)}
        onClose={handleCloseEditMenu}
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(0, 0, 0, 0.05)'
          }
        }}
      >
        <MenuItem onClick={() => handleEditMenuAction('edit')} sx={{ py: 1.5 }}>
          <EditIcon sx={{ mr: 1.5, fontSize: 18 }} />
          Edit room
        </MenuItem>
        <MenuItem onClick={() => handleEditMenuAction('rename')} sx={{ py: 1.5 }}>
          <EditIcon sx={{ mr: 1.5, fontSize: 18 }} />
          Change room type name
        </MenuItem>
        <MenuItem onClick={() => handleEditMenuAction('amenities')} sx={{ py: 1.5 }}>
          <VisibilityIcon sx={{ mr: 1.5, fontSize: 18 }} />
          Manage Amenities
        </MenuItem>
        {editMenuRoom && (
          <Box sx={{ px: 2, py: 1, borderTop: '1px solid rgba(0, 0, 0, 0.08)', borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Current Status:
              <Chip
                size="small"
                label={isRoomActive(editMenuRoom) ? 'Active' : 'Inactive'}
                sx={{
                  ml: 1,
                  backgroundColor: isRoomActive(editMenuRoom) ? 'success.main' : 'text.secondary',
                  color: 'white',
                  fontSize: '0.7rem',
                  height: 20
                }}
              />
            </Typography>
          </Box>
        )}
        {editMenuRoom && isRoomActive(editMenuRoom) ? (
          <MenuItem
            onClick={() => handleEditMenuAction('deactivate')}
            disabled={!!(editMenuRoom?.room_type_category_id && statusUpdateLoading[editMenuRoom.room_type_category_id])}
            sx={{
              py: 1.5,
              color: 'warning.main',
              '&:hover': { backgroundColor: 'warning.light' },
              '&:disabled': { color: 'text.disabled' }
            }}
          >
            {editMenuRoom?.room_type_category_id && statusUpdateLoading[editMenuRoom.room_type_category_id] ? (
              <CircularProgress size={16} sx={{ mr: 1.5, color: 'warning.main' }} />
            ) : (
              <PowerOffIcon sx={{ mr: 1.5, fontSize: 18 }} />
            )}
            {editMenuRoom?.room_type_category_id && statusUpdateLoading[editMenuRoom.room_type_category_id] ? 'Deactivating...' : 'Deactivate'}
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => handleEditMenuAction('activate')}
            disabled={!!(editMenuRoom?.room_type_category_id && statusUpdateLoading[editMenuRoom.room_type_category_id])}
            sx={{
              py: 1.5,
              color: 'success.main',
              '&:hover': { backgroundColor: 'success.light' },
              '&:disabled': { color: 'text.disabled' }
            }}
          >
            {editMenuRoom?.room_type_category_id && statusUpdateLoading[editMenuRoom.room_type_category_id] ? (
              <CircularProgress size={16} sx={{ mr: 1.5, color: 'success.main' }} />
            ) : (
              <PowerSettingsNewIcon sx={{ mr: 1.5, fontSize: 18 }} />
            )}
            {editMenuRoom?.room_type_category_id && statusUpdateLoading[editMenuRoom.room_type_category_id] ? 'Activating...' : 'Activate'}
          </MenuItem>
        )}
      </Menu>

      {/* Status Update Confirmation Dialog */}
      <Dialog 
        open={statusUpdateDialog.open} 
        onClose={() => setStatusUpdateDialog({ open: false, room: null, action: '' })}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#1e293b' }}>
          {statusUpdateDialog.action === 'activate' ? 'Activate' : 'Deactivate'} Room Type
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to {statusUpdateDialog.action} "{statusUpdateDialog.room?.category_name.replace(/_/g, ' ')}"? 
            {statusUpdateDialog.action === 'activate' 
              ? ' This will make the room type available for bookings.' 
              : ' This will make the room type unavailable for bookings.'
            }
          </DialogContentText>
          {statusUpdateDialog.room && (
            <Box sx={{ 
              p: 2, 
              backgroundColor: 'rgba(0, 0, 0, 0.02)', 
              borderRadius: 2, 
              border: '1px solid rgba(0, 0, 0, 0.1)' 
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>Room Type ID:</strong> {statusUpdateDialog.room.room_type_category_id}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Current Status:</strong> 
                <Chip
                  size="small"
                  label={isRoomActive(statusUpdateDialog.room) ? 'Active' : 'Inactive'}
                  sx={{
                    ml: 1,
                    backgroundColor: isRoomActive(statusUpdateDialog.room) ? 'success.main' : 'text.secondary',
                    color: 'white',
                    fontSize: '0.7rem',
                    height: 20
                  }}
                />
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={() => setStatusUpdateDialog({ open: false, room: null, action: '' })}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleStatusUpdate} 
            color={statusUpdateDialog.action === 'activate' ? 'success' : 'warning'} 
            variant="contained"
            disabled={statusUpdateDialog.room && statusUpdateDialog.room.room_type_category_id && statusUpdateLoading[statusUpdateDialog.room.room_type_category_id]}
            sx={{ borderRadius: 2 }}
          >
            {statusUpdateDialog.room && statusUpdateDialog.room.room_type_category_id && statusUpdateLoading[statusUpdateDialog.room.room_type_category_id] ? 
              <CircularProgress size={20} /> : 
              (statusUpdateDialog.action === 'activate' ? 'Activate' : 'Deactivate')
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Amenities Modal */}
      <AmenitiesModal
        open={modalOpen}
        selectedAmenities={modalSelected}
        onChange={setModalSelected}
        onSave={handleSaveAmenities}
        onClose={() => setModalOpen(false)}
        sections={amenitiesSections}
        loading={savingAmenities}
        categoryId={modalRoom?.room_type_category_id}
        roomTypeName={modalRoom?.category_name}
      />

      {/* Edit room dialog */}
      <Dialog 
        open={editDialog.open} 
        onClose={() => {
          if (editLoading) return;
          setEditAmenitiesOpen(false);
          setEditDialog({ open: false, room: null });
        }} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
          }
        }}
      >
        <AmenitiesModal
          open={editAmenitiesOpen}
          selectedAmenities={editAmenities}
          onChange={setEditAmenities}
          onSave={() => setEditAmenitiesOpen(false)}
          onClose={() => setEditAmenitiesOpen(false)}
          sections={amenitiesSections}
          disableApi
          categoryId={editDialog.room?.room_type_category_id}
          roomTypeName={editDialog.room?.category_name}
        />
        <DialogTitle sx={{ 
          fontWeight: 700, 
          fontSize: { xs: '1.25rem', sm: '1.5rem' }, 
          color: '#2E4057', 
          mb: 1, 
          textAlign: 'center',
          borderBottom: '2px solid #FF7700',
          pb: 2
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: { xs: 1, sm: 2 },
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <GroupIcon sx={{ 
              color: '#FF7700', 
              fontSize: { xs: 24, sm: 28 } 
            }} />
            <Typography sx={{ 
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              textAlign: 'center'
            }}>
              Edit room
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {editError && (
            <Alert 
              severity="error" 
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setEditError('')}
            >
              {editError}
            </Alert>
          )}

          <Paper
            elevation={0}
            sx={{ p: 2, mb: 3, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 2 }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Room type
            </Typography>
            <Typography variant="h6" fontWeight={700} color="#2E4057">
              {editDialog.room?.category_name?.replace(/_/g, ' ')}
            </Typography>
          </Paper>

          {editDetailsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#FF7700' }} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                  label="Occupancy"
                  fullWidth
                  value={editForm.occupancy}
                  onChange={(e) => handleEditFormChange('occupancy', e.target.value)}
                  placeholder="Maximum guests"
                  InputProps={{
                    startAdornment: <GroupIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
                <TextField
                  label="Room count"
                  fullWidth
                  value={editForm.room_count}
                  onChange={(e) => handleEditFormChange('room_count', e.target.value)}
                  placeholder="Number of rooms"
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                  label="Start date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={editForm.start_date}
                  onChange={(e) => handleEditFormChange('start_date', e.target.value)}
                  inputProps={{ min: todayStr }}
                />
                <TextField
                  label="End date"
                  type="date"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={editForm.end_date}
                  onChange={(e) => handleEditFormChange('end_date', e.target.value)}
                  inputProps={{ min: editForm.start_date || todayStr }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Room count and rates are applied from the start date through the end date.
              </Typography>

              {parseInt(editForm.occupancy, 10) > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: '#FF7700' }}>
                    Rates per guest
                  </Typography>
                  <Grid container spacing={2}>
                    {Array.from({ length: parseInt(editForm.occupancy, 10) || 0 }, (_, i) => (
                      <Grid item xs={12} sm={6} key={i}>
                        <TextField
                          label={`Rate for ${i + 1} guest${i > 0 ? 's' : ''}`}
                          fullWidth
                          value={editForm.rates[`${i + 1}guest`] || ''}
                          onChange={(e) => handleEditRateChange(`${i + 1}guest`, e.target.value)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#FF7700' }}>
                  Amenities
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setEditAmenitiesOpen(true)}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 700,
                    color: '#FF7700',
                    borderColor: '#FF7700',
                    textTransform: 'none',
                  }}
                >
                  Edit amenities
                </Button>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                  {Object.values(editAmenities).flat().length === 0 ? (
                    <Typography variant="caption" color="text.secondary">No amenities selected</Typography>
                  ) : (
                    Object.values(editAmenities).flat().map((amenity) => (
                      <Chip key={amenity} label={amenity} size="small" sx={{ fontWeight: 600 }} />
                    ))
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          p: { xs: 2, sm: 3 }, 
          pt: 1, 
          gap: { xs: 1, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'center'
        }}>
          <Button 
            onClick={() => setEditDialog({ open: false, room: null })}
            variant="outlined"
            disabled={editLoading}
            fullWidth={isMobile}
            sx={{ 
              borderRadius: 2,
              borderColor: '#6c757d',
              color: '#6c757d',
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveRoomEdit} 
            variant="contained"
            fullWidth={isMobile}
            disabled={editLoading || editDetailsLoading}
            startIcon={editLoading ? <CircularProgress size={16} /> : <EditIcon />}
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #FF7700 0%, #FFA040 100%)',
              '&:hover': { 
                background: 'linear-gradient(135deg, #e65100 0%, #FF7700 100%)',
              },
              '&:disabled': {
                background: '#ccc',
              },
            }}
          >
            {editLoading ? 'Saving...' : 'Save room'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Room Type Dialog */}
      <Dialog 
        open={addModalOpen} 
        onClose={handleCloseAddModal} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 700, 
          fontSize: '1.5rem', 
          color: '#FF7700', 
          mb: 2, 
          textAlign: 'center' 
        }}>
          Add New Room Type
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {addError && (
            <Typography color="error" sx={{ mb: 1, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
              {addError}
            </Typography>
          )}
          
          <Box sx={{ mb: 2, mt: 1 }}>
            <Autocomplete
              fullWidth
              options={roomTypeOptions}
              getOptionLabel={(option) => {
                const name = option.safe_name || option.name || '';
                return name.replace(/_/g, ' ');
              }}
              value={roomTypeOptions.find(opt => opt.id === addForm.room_type_id) || null}
              onChange={(event, newValue) => {
                handleAddFormChange('room_type_id', newValue ? newValue.id : '');
                // Set the search query to show the selected value with proper formatting
                setSearchQuery(newValue ? (newValue.safe_name || newValue.name || '').replace(/_/g, ' ') : '');
              }}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'reset') return;
                setSearchQuery(newInputValue);
              }}
              inputValue={searchQuery}
              loading={isSearching}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Room Type"
                  variant="outlined"
                  placeholder="Search and select room type"
                  sx={{
                    borderRadius: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#FF7700'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#FF7700',
                        borderWidth: 2
                      }
                    }
                  }}
                  inputProps={{
                    ...params.inputProps,
                    style: { fontSize: '1rem' }
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {(option.safe_name || option.name || '').replace(/_/g, ' ')}
                  </Typography>
                </Box>
              )}
              filterOptions={(options, { inputValue }) => {
                const query = normalizeRoomSearchText(inputValue);
                if (!query) return options;
                return options.filter((option) => roomOptionMatchesQuery(option, inputValue));
              }}
              noOptionsText="No room types found"
              loadingText="Loading room types..."
              isOptionEqualToValue={(option, value) => option.id === value.id}
              open={
                searchQuery.trim().length >= 1 &&
                !addForm.room_type_id &&
                (roomTypeOptions.length > 0 || isSearching)
              }
              onOpen={() => {}}
              onClose={() => {
                // Close dropdown when selection is made
                if (addForm.room_type_id) {
                  setSearchQuery('');
                }
              }}
              clearOnBlur={false}
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              label="Occupancy"
              fullWidth
              value={addForm.guests_allowed}
              onChange={e => handleAddFormChange('guests_allowed', e.target.value)}
              sx={{ borderRadius: 2 }}
              variant="outlined"
            />
            <TextField
              label="Room Count"
              fullWidth
              value={addForm.room_count}
              onChange={e => handleAddFormChange('room_count', e.target.value)}
              sx={{ borderRadius: 2 }}
              variant="outlined"
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              label="Start Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={addForm.start_date}
              onChange={e => handleAddFormChange('start_date', e.target.value)}
              inputProps={{ min: todayStr }}
              sx={{ borderRadius: 2 }}
              variant="outlined"
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
              <TextField
                label="End Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={addForm.end_date}
                onChange={e => handleAddFormChange('end_date', e.target.value)}
                inputProps={{ min: addForm.start_date || todayStr }}
                sx={{ borderRadius: 2 }}
                variant="outlined"
              />
              <Button
                variant="outlined"
                size="small"
                onClick={handleAddTwoYears}
                disabled={hasAddedTwoYears || !addForm.start_date}
                sx={{
                  borderRadius: 2,
                  borderColor: '#FF7700',
                  color: '#FF7700',
                  fontSize: '0.75rem',
                  py: 0.5,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 119, 0, 0.1)',
                    borderColor: '#FF7700'
                  },
                  '&:disabled': {
                    borderColor: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.38)'
                  }
                }}
              >
                {hasAddedTwoYears ? '2 Years Added' : 'Add 2 Years'}
              </Button>
            </Box>
          </Box>
          
          {addForm.guests_allowed > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#FF7700' }}>
                Rates per Guest
              </Typography>
              <Grid container spacing={2}>
                {Array.from({ length: parseInt(addForm.guests_allowed) || 0 }, (_, i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <TextField
                      label={`Rate for ${i + 1} Guest${i > 0 ? 's' : ''}`}
                      fullWidth
                      value={addForm.rates[`${i + 1}guest`] || ''}
                      onChange={e => handleRateChange(`${i + 1}guest`, e.target.value)}
                      sx={{ borderRadius: 2 }}
                      variant="outlined"
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
                      )}
            {/* Amenities selection UI */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#FF7700' }}>Amenities</Typography>
              <Button 
                variant="outlined"
                startIcon={<AddIcon />}
                sx={{ borderRadius: 3, fontWeight: 700, color: '#FF7700', borderColor: '#FF7700', mb: 1, '&:hover': { background: 'rgba(255,119,0,0.08)', borderColor: '#FF7700' } }}
                onClick={() => setAddAmenitiesModalOpen(true)}
              >
                Add Amenities
              </Button>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {Object.values(addAmenities).flat().length === 0 ? (
                  <Typography variant="caption" color="text.secondary">No amenities selected</Typography>
                ) : (
                  Object.values(addAmenities).flat().map(am => (
                    <Chip key={am} label={am} size="small" color="primary" variant="filled" sx={{ fontWeight: 500, fontSize: '0.95rem', px: 1.2, mb: 0.5, borderRadius: 2 }} />
                  ))
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', mt: 2 }}>
            <Button onClick={handleCloseAddModal} disabled={addLoading} sx={{ borderRadius: 3, fontWeight: 700 }}>Cancel</Button>
            <Button onClick={handleAddRoomType} variant="contained" disabled={addLoading} sx={{ fontWeight: 700, borderRadius: 3, background: 'linear-gradient(90deg, #FF7700 0%, #FFA040 100%)', color: 'white', boxShadow: '0 2px 8px 0 rgba(255,119,0,0.10)' }}>
              {addLoading ? <CircularProgress size={20} /> : 'Add'}
            </Button>
          </DialogActions>
          {/* Amenities Modal for Add Room Type */}
          <AmenitiesModal
            open={addAmenitiesModalOpen}
            selectedAmenities={addAmenities}
            onChange={setAddAmenities}
            onSave={() => setAddAmenitiesModalOpen(false)}
            onClose={() => setAddAmenitiesModalOpen(false)}
            sections={amenitiesSections}
            loading={false}
            categoryId={null}
            roomTypeName={roomTypeOptions.find(opt => opt.id === Number(addForm.room_type_id))?.safe_name || roomTypeOptions.find(opt => opt.id === Number(addForm.room_type_id))?.name}
            disableApi={true}
          />
        </Dialog>

      {/* Rename Room Type Dialog */}
      <Dialog 
        open={renameDialog.open} 
        onClose={handleCloseRenameDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 700, 
          color: '#1e293b',
          fontSize: '1.25rem',
          pb: 2,
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
        }}>
          Change Room Type Name
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Current Name:
            </Typography>
            <Typography variant="body1" sx={{ 
              fontWeight: 600, 
              color: '#1e293b',
              p: 1.5,
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              borderRadius: 1,
              border: '1px solid rgba(0, 0, 0, 0.1)'
            }}>
              {renameDialog.room?.category_name?.replace(/_/g, ' ')}
            </Typography>
          </Box>
          
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Select New Room Type:
            </Typography>
            <Autocomplete
              fullWidth
              options={availableRoomTypes}
              getOptionLabel={(option) => option.category_name?.replace(/_/g, ' ') || ''}
              value={renameDialog.selectedRoomType}
              onChange={(event, newValue) => {
                setRenameDialog(prev => ({ 
                  ...prev, 
                  selectedRoomType: newValue,
                  newName: newValue ? newValue.category_name?.replace(/_/g, ' ') : ''
                }));
              }}
              loading={loadingRoomTypes}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search and select room type"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#FF7700'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#FF7700',
                        borderWidth: 2
                      }
                    }
                  }}
                  inputProps={{
                    ...params.inputProps,
                    style: { fontSize: '1rem' }
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {option.category_name?.replace(/_/g, ' ')}
                  </Typography>
                </Box>
              )}
              filterOptions={(options, { inputValue }) => {
                if (!inputValue) return options;
                
                const searchTerms = inputValue.toLowerCase().trim().split(/\s+/);
                
                return options.filter(option => {
                  const name = (option.category_name || '').toLowerCase();
                  const type = (option.type || '').toLowerCase();
                  
                  // Check if all search terms are found in name or type fields only
                  return searchTerms.every(term => 
                    name.includes(term) || 
                    type.includes(term)
                  );
                });
              }}
              noOptionsText="No room types found"
              loadingText="Loading room types..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          pt: 1,
          gap: 1
        }}>
          <Button
            onClick={handleCloseRenameDialog}
            variant="outlined"
            sx={{
              borderColor: 'rgba(0, 0, 0, 0.2)',
              color: '#64748b',
              '&:hover': {
                borderColor: '#64748b',
                backgroundColor: 'rgba(100, 116, 139, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRenameRoomType}
            variant="contained"
            disabled={renaming || !renameDialog.selectedRoomType || renameDialog.selectedRoomType?.id === renameDialog.room?.room_type_category_id}
            sx={{
              background: 'linear-gradient(90deg, #FF7700 0%, #FFA040 100%)',
              color: 'white',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(90deg, #FF7700 0%, #FF7700 100%)',
              },
              '&:disabled': {
                background: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.38)'
              }
            }}
          >
            {renaming ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                <span>Updating...</span>
              </Box>
            ) : (
              'Update Name'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoomTypes; 